// TeamCard — one team in the public Team Visualization overview grid
// (PRODOPSAI-529; redesigned PRODOPSAI-534). Presentational only.
//
// Identity: a coloured left accent bar + monogram tile (deterministic per
// team). Shows EVERY member as an initials badge (no "+N more" — the card
// grows vertically), plus compact abbreviation role chips. Departed members
// (isActive=false) render greyed.
//
// Non-interactive on its own (no click handler → no S6847). The overview grid
// (TeamOverviewGrid) wraps each card in a button to open the detail view.

import { HoverTip } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { ITeamMembershipTreeTeam } from '@skystone/shared';
import {
  distinctTeamMembers,
  initials,
  memberCountLabel,
  memberTooltip,
  roleChipSummary,
} from './team-card-helpers';
import { teamAccent, teamMonogram } from './team-identity';

interface TeamCardProps {
  team: ITeamMembershipTreeTeam;
}

export function TeamCard({ team }: Readonly<TeamCardProps>) {
  const members = distinctTeamMembers(team);
  const chips = roleChipSummary(team.roleGroups);
  const accent = teamAccent(team.team.regionName);
  const monogram = teamMonogram(team.team.name);

  return (
    <article
      className="relative h-full rounded-xl border border-border bg-card py-4 pl-5 pr-4 shadow-sm"
      data-testid="team-card"
      data-team-id={team.team.id}
    >
      <span
        className={cn('absolute inset-y-0 left-0 w-1.5 rounded-l-xl', accent.bar)}
        aria-hidden="true"
      />

      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex h-9 w-9 flex-none items-center justify-center rounded-lg text-sm font-bold',
            accent.tileBg,
            accent.tileText,
          )}
          aria-hidden="true"
        >
          {monogram}
        </span>
        <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
          <h3 className="truncate text-sm font-semibold text-foreground">{team.team.name}</h3>
          <span className="flex-none text-xs text-muted-foreground">
            {memberCountLabel(team.memberCount)}
          </span>
        </div>
      </div>

      {members.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1" data-testid="member-initials">
          {members.map((member) => (
            <HoverTip key={member.person.personId} content={memberTooltip(member)} side="top">
              <span
                data-testid="member-badge"
                role="img"
                aria-label={memberTooltip(member)}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold',
                  member.person.isActive
                    ? cn(accent.badgeBg, accent.badgeText)
                    : 'bg-muted/50 text-muted-foreground/50',
                )}
              >
                {initials(member.person.displayName)}
              </span>
            </HoverTip>
          ))}
        </div>
      )}

      {chips.length > 0 && (
        <div
          className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px]"
          data-testid="role-chips"
        >
          {chips.map((chip, index) => (
            <span key={chip.key} className="inline-flex items-center" title={`${chip.label} (${chip.count})`}>
              {index > 0 && <span aria-hidden="true" className="mr-1.5 text-border">•</span>}
              <span className="font-semibold text-muted-foreground">{chip.abbrev}</span>
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
